"use client";

import { OptionType } from "@/components/select-multi";
import Autocomplete, { AutocompleteProps } from "@/components/ui/autocomplete";
import { MultiSelect } from "@/components/ui/multi-select";
import { api, RouterOutputs } from "@/lib/trpc/client";
import { ProcedureValueResolver } from "@ept/shared/src/procedures";
import _ from "lodash";
import React from "react";
import { useDebounce } from "use-debounce";
export type BaseResolverProps = {
  onValue: (option: {
    value: string;
    label: string;
  } | {
    value: string;
    label: string;
  }[]) => void;
  institutionId: string;
  runningProcedureId?: string;
  isServant?: boolean;
  isMulti?: boolean;
  type: ProcedureValueResolver;
  referenceOptions?: {
    value: string;
    label: string;
    type: "reference";
  }[];
  useReferenceOptions?: boolean;
  extraStyles?: string;
} & Omit<AutocompleteProps, "options" | "onChange">;
export const BaseResolver = (props: BaseResolverProps) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchQueryDebounced] = useDebounce(searchQuery, 500);
  const routerToUse = props.isServant ? "institutionProceduresRouter" : "adminProceduresRouter";
  const initialValue = props.initialValue ? Array.isArray(props.initialValue) ? props.initialValue : [props.initialValue] : null;

  // @ts-expect-error
  const prefetchBaseQuery = api[routerToUse].getResolverData.useQuery({
    institutionId: props.institutionId,
    type: props.type,
    runningProcedureId: props.runningProcedureId,
    prefetchId: initialValue
  }, {
    enabled: !!props.initialValue
  }) as {
    data: RouterOutputs["institutionProceduresRouter"]["getResolverData"];
  };

  // @ts-expect-error
  const resolverData = api[routerToUse].getResolverData.useQuery({
    searchQuery: searchQueryDebounced,
    institutionId: props.institutionId,
    runningProcedureId: props.runningProcedureId,
    type: props.type
  }, {
    enabled: searchQueryDebounced.length > 3
  }) as {
    data: RouterOutputs["institutionProceduresRouter"]["getResolverData"];
  };
  const apiOptions = React.useMemo(() => {
    if (!resolverData.data?.length) {
      return prefetchBaseQuery.data ?? [];
    }
    return resolverData.data.concat(prefetchBaseQuery.data ?? []);
  }, [resolverData.data, prefetchBaseQuery.data]);
  const searchOptions = React.useMemo(() => {
    return _.uniqBy(apiOptions.map(option => ({
      ...option,
      label: option.label.toString(),
      type: "api" as const,
      subtitle: option.subtitle?.toString()
    }))
    // @ts-expect-error
    .concat(props.useReferenceOptions && props.referenceOptions || []), "value");
  }, [apiOptions, props.referenceOptions]);
  return props.isMulti ? <MultiSelect {...props} isServer selected={searchOptions.filter(option => initialValue?.includes(option.value))} options={searchOptions} onChange={val => props.onValue(val as OptionType[])} onQueryChange={setSearchQuery} data-sentry-element="MultiSelect" data-sentry-component="BaseResolver" data-sentry-source-file="BaseResolver.tsx" /> : <Autocomplete {...props} isServer initialValue={props.initialValue ?? ""} options={searchOptions} onQueryChange={setSearchQuery} onChangeAction={val => props.onValue(val as OptionType)} data-sentry-element="Autocomplete" data-sentry-component="BaseResolver" data-sentry-source-file="BaseResolver.tsx" />;
};